import React from 'react';

const DEFAULT_WIDTH = 720;
const DEFAULT_HEIGHT = 420;
const MARGINS = {
  top: 24,
  right: 24,
  bottom: 72,
  left: 76,
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const PriebeChartSvg = ({ chart, width = DEFAULT_WIDTH, height = DEFAULT_HEIGHT }) => {
  if (!chart) return null;

  const plotWidth = width - MARGINS.left - MARGINS.right;
  const plotHeight = height - MARGINS.top - MARGINS.bottom;
  const [xMin, xMax] = chart.xDomain || [0, 1];
  const [yMin, yMax] = chart.yDomain || [0, 1];

  const mapX = (value) => {
    if (chart.xScale === 'log') {
      const logMin = Math.log10(xMin);
      const logMax = Math.log10(xMax);
      const safeValue = clamp(value, xMin, xMax);
      const ratio = (Math.log10(safeValue) - logMin) / (logMax - logMin || 1);
      return MARGINS.left + ratio * plotWidth;
    }

    const ratio = (value - xMin) / (xMax - xMin || 1);
    return MARGINS.left + ratio * plotWidth;
  };

  const mapY = (value) => {
    const ratio = (value - yMin) / (yMax - yMin || 1);
    return MARGINS.top + plotHeight - ratio * plotHeight;
  };

  const getPath = (points) =>
    (points || [])
      .map((point, index) => `${index === 0 ? 'M' : 'L'} ${mapX(point.x)} ${mapY(point.y)}`)
      .join(' ');

  const renderCurve = (curve, index, isHighlight = false) => {
    const stroke = curve?.style?.stroke || (isHighlight ? '#ef6c00' : '#4b5563');
    const strokeWidth = curve?.style?.strokeWidth || (isHighlight ? 2.5 : 1.4);
    const strokeDasharray = curve?.style?.strokeDasharray || 'none';

    return (
      <path
        key={`${curve?.label || 'curve'}-${index}`}
        d={getPath(curve.points)}
        fill="none"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeDasharray={strokeDasharray}
        vectorEffect="non-scaling-stroke"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    );
  };

  return (
    <svg
      width="100%"
      height="100%"
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={chart.title}
      style={{ display: 'block', width: '100%', height: 'auto', background: '#fff', borderRadius: 8 }}
    >
      <rect x="0" y="0" width={width} height={height} fill="#ffffff" rx="8" />
      <rect
        x={MARGINS.left}
        y={MARGINS.top}
        width={plotWidth}
        height={plotHeight}
        fill="#fafafa"
        stroke="#d1d5db"
        strokeWidth="1"
      />

      {(chart.xTicks || []).map((tick) => {
        const x = mapX(tick);
        return (
          <g key={`x-${tick}`}>
            <line x1={x} y1={MARGINS.top} x2={x} y2={MARGINS.top + plotHeight} stroke="#e5e7eb" strokeWidth="1" />
            <text x={x} y={MARGINS.top + plotHeight + 18} textAnchor="middle" fontSize="12" fill="#374151">
              {tick}
            </text>
          </g>
        );
      })}

      {(chart.yTicks || []).map((tick) => {
        const y = mapY(tick);
        return (
          <g key={`y-${tick}`}>
            <line x1={MARGINS.left} y1={y} x2={MARGINS.left + plotWidth} y2={y} stroke="#e5e7eb" strokeWidth="1" />
            <text x={MARGINS.left - 12} y={y + 4} textAnchor="end" fontSize="12" fill="#374151">
              {tick}
            </text>
          </g>
        );
      })}

      {(chart.curves || []).map((curve, index) => renderCurve(curve, index, false))}
      {(chart.highlightCurves || []).map((curve, index) => renderCurve(curve, index, true))}

      {(chart.selectedPoints || []).map((point, index) => {
        const x = mapX(point.x);
        const y = mapY(point.y);
        return (
          <g key={`point-${index}`}>
            <line x1={x} y1={MARGINS.top} x2={x} y2={MARGINS.top + plotHeight} stroke="#f59e0b" strokeDasharray="5 4" />
            <line x1={MARGINS.left} y1={y} x2={MARGINS.left + plotWidth} y2={y} stroke="#f59e0b" strokeDasharray="5 4" />
            <circle cx={x} cy={y} r="4" fill="#ef6c00" />
            <rect
              x={Math.min(x + 8, width - 150)}
              y={Math.max(y - 24, MARGINS.top + 4)}
              width="138"
              height="18"
              rx="4"
              fill="rgba(255,255,255,0.92)"
              stroke="#fed7aa"
            />
            <text x={Math.min(x + 14, width - 144)} y={Math.max(y - 11, MARGINS.top + 17)} fontSize="11" fill="#9a3412">
              {point.label}
            </text>
          </g>
        );
      })}

      <line
        x1={MARGINS.left}
        y1={MARGINS.top + plotHeight}
        x2={MARGINS.left + plotWidth}
        y2={MARGINS.top + plotHeight}
        stroke="#111827"
        strokeWidth="1.5"
      />
      <line x1={MARGINS.left} y1={MARGINS.top} x2={MARGINS.left} y2={MARGINS.top + plotHeight} stroke="#111827" strokeWidth="1.5" />

      <text x={MARGINS.left + plotWidth / 2} y={height - 18} textAnchor="middle" fontSize="14" fontWeight="700" fill="#111827">
        {chart.xLabel}
      </text>
      <text
        x="18"
        y={MARGINS.top + plotHeight / 2}
        transform={`rotate(-90 18 ${MARGINS.top + plotHeight / 2})`}
        textAnchor="middle"
        fontSize="14"
        fontWeight="700"
        fill="#111827"
      >
        {chart.yLabel}
      </text>
    </svg>
  );
};

export default React.memo(PriebeChartSvg);
